import React, { useState } from "react";
import { utils } from "../../../utils/utils";
import SequencesTable from "./SequencesTable";
import { useAuth } from "../../../providers/authProvider";
import { Container, Button } from "reactstrap";
import InformationModal from "../../InformationModal";
import ConfirmationModal from "../../ConfirmationModal";
import SequenceDetailsModal from "./SequenceDetailsModal";
import { sequenceApi } from "../../../services/sequenceServices";

const WAITING_FOR_SCRUBBING_STATUS = 8;
const LOOK_OVER_STATUS = 11;
const CHECKING_COMPLETE_STATUS = 6;
const LOOK_OVER_TAB = "LOOK_OVER_TAB";

const LookOver = ({
  sequencesTotal,
  setSequencesTotal,
  reloadTabs,
  setReloadTabs,
  tabName,
  search = "",
}) => {
  const [authContext] = useAuth();
  const userId = authContext?.currentUser?.id;
  const [detailModal, setDetailModal] = useState(false);
  const [checkerId, setCheckerId] = useState();
  const [detailerId, setDetailerId] = useState();
  const [sequenceStatusId, setSequenceStatusId] = useState();
  const [sequenceSelected, setSequenceSelected] = useState();
  const [informationModal, setInformationModal] = useState(
    utils.initInformationModal
  );
  const [confirmationModal, setConfirmationModal] = useState(
    utils.initConfirmationModal
  );

  const columns = [
    {
      accessor: "sendToScrubbingButton",
      Header: "",
      headerProps: { className: "text-truncate" },
      disableSortBy: true,
      width: 135,
      Cell: (rowData) => {
        const sequence = rowData.row.original;
        const canPerform =
          utils.canUserPerformDetailingAction(
            sequence,
            LOOK_OVER_STATUS,
            userId
          ) || utils.isAdminRole(authContext.currentUser?.role?.id);
        if (!canPerform) return null;
        return (
          <Button
            className="btn-sm rounded px-1 py-0"
            color={"warning"}
            onClick={() => {
              const sequence = rowData.row.original;
              changeSequenceStatus(
                sequence,
                WAITING_FOR_SCRUBBING_STATUS,
                sequence?.detailer?.id ?? null,
                sequence?.checker?.id ?? null
              );
            }}
          >
            Send to Scrubbing
          </Button>
        );
      },
    },
    {
      accessor: "readyForReviewButton",
      Header: "",
      headerProps: { className: "text-truncate" },
      disableSortBy: true,
      width: 135,
      Cell: (rowData) => {
        const sequence = rowData.row.original;
        const canPerform =
          utils.canUserPerformDetailingAction(
            sequence,
            LOOK_OVER_STATUS,
            userId
          ) || utils.isAdminRole(authContext.currentUser?.role?.id);
        if (!canPerform) return null;
        return (
          <Button
            className="btn-sm rounded px-1 py-0"
            color={"success"}
            onClick={() => {
              const sequence = rowData.row.original;
              changeSequenceStatus(
                sequence,
                CHECKING_COMPLETE_STATUS,
                sequence?.detailer?.id ?? null,
                sequence?.checker?.id ?? null
              );
            }}
          >
            Ready for Review
          </Button>
        );
      },
    },
    {
      accessor: "seeJobDetailsButton",
      Header: "",
      headerProps: { className: "text-truncate" },
      disableSortBy: true,
      width: 130,
      Cell: (rowData) => {
        return (
          <Button
            className="btn-sm rounded px-1 py-0"
            color={"primary"}
            onClick={() => {
              const sequence = rowData.row.original;
              setSequenceSelected(sequence);
              setDetailerId(sequence?.detailer?.id || null);
              setCheckerId(sequence?.checker?.id || null);
              setSequenceStatusId(sequence?.sequenceStatusId);
              setDetailModal(true);
            }}
          >
            See Job Details
          </Button>
        );
      },
    },
  ];

  const changeSequenceStatus = async (
    sequence,
    sequenceStatusId,
    effectiveDetailerId = detailerId,
    effectiveCheckerId = checkerId
  ) => {
    try {
      let title, successMessage, confirmationMessage;

      if (sequenceStatusId === WAITING_FOR_SCRUBBING_STATUS) {
        title = "Send to Scrubbing";
        successMessage = detailModal ? "updated" : "sent to Scrubbing";
        confirmationMessage = detailModal
          ? "Do you confirm you want to update the sequence " +
            sequenceSelected?.job?.jobNumber
          : "Are you sure you want to send this job to Scrubbing?";
      } else if (sequenceStatusId === CHECKING_COMPLETE_STATUS) {
        title = "Ready for Review";
        successMessage = detailModal ? "updated" : "sent to Review";
        confirmationMessage = detailModal
          ? "Do you confirm you want to update the sequence " +
            sequenceSelected?.job?.jobNumber
          : "Are you sure you are ready to send this job to Review?";
      }

      setConfirmationModal({
        isOpen: true,
        onSubmit: async () => {
          try {
            setReloadTabs([]);
            const { detailer, checker, ...sequenceFields } = sequence;
            await sequenceApi.updateSequence(sequence.id, {
              ...sequenceFields,
              detailerId: effectiveDetailerId,
              checkerId: effectiveCheckerId,
              sequenceStatusId,
            });
            setConfirmationModal(utils.initConfirmationModal);
            setInformationModal({
              isOpen: true,
              title,
              body: `Sequence ${successMessage} successfully.`,
            });
            setReloadTabs([
              LOOK_OVER_TAB,
              utils.detailingTabs[sequenceStatusId],
            ]);
          } catch (error) {
            onClear();
            setInformationModal({
              isOpen: true,
              title: "Error",
              body: "There was an error with your request.",
            });
          }
        },
        onClose: onClear,
        title,
        body: `<div class="text-center">${confirmationMessage}</div>`,
      });
    } catch (error) {
      onClear();
      setInformationModal({
        isOpen: true,
        title: "Error",
        body: "There was an error with your request.",
      });
    }
  };

  const onClear = async () => {
    setDetailerId();
    setCheckerId();
    setConfirmationModal(utils.initConfirmationModal);
    setInformationModal(utils.initInformationModal);
    setSequenceStatusId();
    setSequenceSelected();
    setDetailModal(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    changeSequenceStatus(
      sequenceSelected,
      sequenceStatusId,
      detailerId,
      checkerId
    );
  };

  return (
    <Container fluid className="px-0">
      <SequencesTable
        columns={columns}
        sequenceStatusId={LOOK_OVER_STATUS}
        setSequencesTotal={setSequencesTotal}
        sequencesTotal={sequencesTotal}
        reloadTabs={reloadTabs}
        tabName={tabName}
        search={search}
      />
      {informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={() => onClear()}
        />
      ) : confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : (
        <SequenceDetailsModal
          sequence={sequenceSelected}
          detailModal={detailModal}
          onSubmit={onSubmit}
          onClear={onClear}
          checkerId={checkerId}
          setCheckerId={setCheckerId}
          detailerId={detailerId}
          setDetailerId={setDetailerId}
          enableUpdate={
            utils.canUserPerformDetailingAction(
              sequenceSelected,
              sequenceStatusId,
              userId
            ) || utils.isAdminRole(authContext.currentUser?.role?.id)
          }
          setInformationModal={setInformationModal}
          statusId={sequenceStatusId}
          setStatusId={setSequenceStatusId}
        />
      )}
    </Container>
  );
};

export default LookOver;
